import { AlertTriangle, Bell, FileText, Mail, MessageSquare, Tag, Trash2 } from 'lucide-react';
import type { NavigateOptions } from '@tanstack/react-router';
import {
  isCommentNotificationType,
  normalizeNotificationType,
} from '@/lib/commentNotificationUtils';
import { acknowledgeInboxNotificationId } from '@/lib/inboxAcknowledgedNotifications';
import {
  resolveUserMentionsInCommentText,
  type MentionUser,
} from '@/lib/data-transformers/comment';
import { queueMemberChatMessageScroll } from '@/lib/groupChatNavigation';
import useChatStore from '@/stores/useChatStore';
import type {
  NotificationUser,
  UserNotification,
  UserNotificationDetails,
} from '@/types/notifications';
import type { UserDetails } from '@/types/user';

const FOLDER_LIKE_LIST_ITEM_TYPES = new Set([
  'FOLDER',
  'TASK',
  'AI_TASK',
  'EPIC_TASK',
  'AI_TRANSCODE_TASK',
  'ORCHESTRATOR_TASK',
]);

/**
 * Returns true when a name field is missing or the API literal "null".
 */
function isNullishNamePart(value?: string): boolean {
  if (!value) return true;
  const normalized = value.trim().toLowerCase();
  return normalized === '' || normalized === 'null';
}

/**
 * Resolves a display name for notification users when first/last name are absent.
 */
export function getNotificationUserDisplayName(
  user: Pick<NotificationUser, 'firstName' | 'lastName' | 'username' | 'email'>
): string {
  const first = !isNullishNamePart(user.firstName) ? user.firstName!.trim() : '';
  const last = !isNullishNamePart(user.lastName) ? user.lastName!.trim() : '';
  const fullName = [first, last].filter(Boolean).join(' ');
  if (fullName) return fullName;
  if (user.username) {
    const localPart = user.username.split('@')[0];
    return localPart || user.username;
  }
  const emailLocal = user.email.split('@')[0];
  return emailLocal || user.email;
}

/**
 * Avatar initials derived from the resolved display name.
 */
/**
 * Maps API notification user payload to UserHoverCard's UserDetails shape.
 */
export function toNotificationUserHoverDetails(user: NotificationUser): UserDetails {
  const displayName = getNotificationUserDisplayName(user);
  const [firstName = displayName, lastName = ''] = displayName.split(/\s+/, 2);

  return {
    id: user.id,
    username: user.username,
    firstName,
    lastName: lastName || firstName,
    email: user.email,
    mobileNo: '',
    isEnabled: user.isEnabled,
    isBlocked: user.isBlocked,
    role: user.role,
    organization: user.role,
  };
}

export function getNotificationUserInitials(user: NotificationUser): string {
  const displayName = getNotificationUserDisplayName(user);
  const parts = displayName.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return (displayName[0] || '?').toUpperCase();
}

/**
 * Prefer notifiedAt for UI timestamps; fall back to createdAt.
 */
export function getNotificationDisplayTimestamp(
  notification: Pick<UserNotification, 'notifiedAt' | 'createdAt'>
): number {
  return notification.notifiedAt ?? notification.createdAt;
}

/**
 * Strips sender prefixes from notification body text (including API "null null").
 */
export function getNotificationBodyPreview(
  body: string,
  sender: NotificationUser,
  mentionUsers?: MentionUser[],
): string {
  const displayName = getNotificationUserDisplayName(sender);
  const prefixes = [
    `${displayName} `,
    'null null ',
  ];

  if (sender.firstName && sender.lastName) {
    prefixes.push(`${sender.firstName} ${sender.lastName} `);
  }

  let preview = body;
  for (const prefix of prefixes) {
    if (body.startsWith(prefix)) {
      preview = body.slice(prefix.length);
      break;
    }
  }

  return resolveUserMentionsInCommentText(preview, mentionUsers);
}

export type NotificationNavigationTarget = {
  groupId: string;
  /** Omitted for group-scoped member chat (`listItemId = -1`). */
  folderId?: string;
  scrollToItemId?: string;
  scrollToMessageId?: string;
  openMemberChat?: boolean;
};

type NavigateFn = (options: NavigateOptions) => void;

/**
 * Resolves folder route for comment notifications (group chat or item-scoped).
 */
function resolveCommentNotificationFolderId(
  notification: UserNotification
): string | undefined {
  if (notification.parentId != null) {
    const parentId = String(notification.parentId);
    if (parentId !== '0' && parentId !== '-1') {
      return parentId;
    }
  }

  if (notification.listItemId == null) {
    return undefined;
  }

  const listItemId = String(notification.listItemId);
  if (listItemId === '0' || listItemId === '-1') {
    return undefined;
  }

  return listItemId;
}

/**
 * Resolves TanStack Router navigation for a notification click.
 */
export function resolveNotificationNavigationTarget(
  notification: UserNotification
): NotificationNavigationTarget | null {
  const groupId = notification.groupId.toString();

  if (isCommentNotificationType(notification.notificationType)) {
    const scrollToMessageId =
      notification.commentId != null ? String(notification.commentId) : undefined;

    return {
      groupId,
      folderId: resolveCommentNotificationFolderId(notification),
      openMemberChat: true,
      scrollToMessageId,
    };
  }

  const itemType = notification.listItemType?.toUpperCase();

  if (itemType && FOLDER_LIKE_LIST_ITEM_TYPES.has(itemType) && notification.listItemId != null) {
    return {
      groupId,
      folderId: notification.listItemId.toString(),
    };
  }

  if (notification.parentId != null && notification.listItemId != null) {
    return {
      groupId,
      folderId: notification.parentId.toString(),
      scrollToItemId: notification.listItemId.toString(),
    };
  }

  if (notification.listItemId != null) {
    return {
      groupId,
      folderId: notification.listItemId.toString(),
    };
  }

  return null;
}

/**
 * Whether a notification should navigate on inbox click (deleted items do not).
 */
export function isNotificationNavigable(notification: UserNotification): boolean {
  if (normalizeNotificationType(notification.notificationType) === 'list_item_deleted') {
    return false;
  }

  return resolveNotificationNavigationTarget(notification) !== null;
}

export type InboxNotificationActionDeps = {
  navigate: NavigateFn;
  markAsRead: (params: { notificationIds: number[]; groupId: number }) => void;
  acknowledgeNotification?: (notificationId: number) => void;
};

/**
 * Marks an unread notification as read and navigates when the target is navigable.
 */
export function processInboxNotificationAction(
  notification: UserNotification,
  deps: InboxNotificationActionDeps,
): void {
  if (!notification.isRead) {
    acknowledgeInboxNotificationId(notification.id);
    deps.acknowledgeNotification?.(notification.id);
    deps.markAsRead({
      notificationIds: [notification.id],
      groupId: notification.groupId,
    });
  }

  if (!isNotificationNavigable(notification)) {
    return;
  }

  const target = resolveNotificationNavigationTarget(notification);
  if (!target) {
    return;
  }

  executeNotificationNavigation(target, deps.navigate);
}

/**
 * Navigates to the target route and opens member chat, scrolling to a message when provided.
 */
export function executeNotificationNavigation(
  target: NotificationNavigationTarget,
  navigate: NavigateFn
): void {
  if (target.openMemberChat) {
    useChatStore.getState().setMemberChatOpen(true);
  }

  if (target.scrollToMessageId) {
    queueMemberChatMessageScroll(target.scrollToMessageId);
  }

  if (!target.folderId) {
    navigate({
      to: '/group/$groupId',
      params: { groupId: target.groupId },
    });
    return;
  }

  if (target.scrollToItemId) {
    navigate({
      to: '/group/$groupId/item/$folderId',
      params: {
        groupId: target.groupId,
        folderId: target.folderId,
      },
      search: {
        scrollTo: target.scrollToItemId,
      },
    });
    return;
  }

  navigate({
    to: '/group/$groupId/item/$folderId',
    params: {
      groupId: target.groupId,
      folderId: target.folderId,
    },
  });
}

/**
 * Format timestamp to relative time (for V1)
 */
export function formatRelativeTime(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
}

/**
 * Format timestamp to German relative time (for V4)
 * Examples: "2 Std." (2 hours), "5 Min." (5 minutes), "3 Tage" (3 days)
 */
export function formatGermanRelativeTime(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'Gerade eben';
  if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} Min.`;
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} Std.`;
  }
  if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} ${days === 1 ? 'Tag' : 'Tage'}`;
  }
  return date.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' });
}

/**
 * Format timestamp to full date/time (for V2)
 */
export function formatFullDateTime(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Get unread notification IDs from notifications array
 */
export function getUnreadNotificationIds(
  notifications: UserNotificationDetails['notifications']
): number[] {
  return notifications
    .filter((notification) => !notification.isRead)
    .map((notification) => notification.id);
}

/**
 * Calculate pagination info
 */
export function getPaginationInfo(pageNo: number, pageSize: number, totalItems: number) {
  const startItem = (pageNo - 1) * pageSize + 1;
  const endItem = Math.min(pageNo * pageSize, totalItems);
  const totalPages = Math.ceil(totalItems / pageSize);

  return {
    startItem,
    endItem,
    totalPages,
    hasNextPage: pageNo < totalPages,
    hasPrevPage: pageNo > 1,
  };
}

/**
 * Get notification type info for V2
 */
export function getNotificationTypeInfo(type: string) {
  switch (type) {
    case 'list_item_added':
      return {
        icon: FileText,
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        label: 'Item Added',
      };
    case 'list_item_deleted':
      return {
        icon: Trash2,
        color: 'bg-red-100 text-red-800 border-red-200',
        label: 'Item Deleted',
      };
    case 'list_item_updated':
      return {
        icon: FileText,
        color: 'bg-purple-100 text-purple-800 border-purple-200',
        label: 'Item Updated',
      };
    case 'list_item_moved':
      return {
        icon: AlertTriangle,
        color: 'bg-red-100 text-red-800 border-red-200',
        label: 'Item Moved',
      };
    case 'list_item_renamed':
      return {
        icon: Mail,
        color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
        label: 'Item Renamed',
      };
    case 'added_a_comment':
      return {
        icon: MessageSquare,
        color: 'bg-green-100 text-green-800 border-green-200',
        label: 'Added a Comment',
      };
    case 'tagged_in_comment':
    case 'tagged_in_group_chat':
      return {
        icon: Tag,
        color: 'bg-green-100 text-green-800 border-green-200',
        label: 'Tagged in Comment',
      };
    case 'added_a_group_chat_message':
    case 'group_chat_message':
      return {
        icon: MessageSquare,
        color: 'bg-green-100 text-green-800 border-green-200',
        label: 'New Comment',
      };
    default:
      return {
        icon: Bell,
        color: 'bg-gray-100 text-gray-800 border-gray-200',
        label: type,
      };
  }
}

/**
 * Get formatted label for notification type
 */
export function getFormattedLabel(typeInfo: ReturnType<typeof getNotificationTypeInfo>): string {
  const label = typeInfo.label;
  if (label === 'list_item_added') return 'Item Added';
  if (label === 'list_item_deleted') return 'Item Deleted';
  if (label === 'list_item_updated') return 'Item Updated';
  if (label === 'list_item_moved') return 'Item Moved';
  if (label === 'list_item_renamed') return 'Item Renamed';
  if (label === 'list_item_restored') return label;
  return label;
}
